import { useCallback, useEffect, useState } from 'react'
import {
  getProgramApplication,
  getProgramApplications,
  updateProgramApplication,
  type ProgramApplicationDetail,
  type ProgramApplicationFilters,
  type ProgramApplicationStatus,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { ProgramApplicationDetailModal } from './ProgramApplicationDetailModal'

const PAGE_SIZE = 25

const PROGRAM_OPTIONS: { value: string; label: string }[] = [
  { value: 'booth_fund', label: 'Booth Fund' },
]

const STATUS_OPTIONS: { value: ProgramApplicationStatus | ''; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All statuses' },
]

const STATUS_BADGE: Record<ProgramApplicationStatus, string> = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  paid: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  rejected: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
}

function formatCurrencyCents(cents: unknown): string {
  if (typeof cents !== 'number') return '—'
  return `$${(cents / 100).toFixed(2)}`
}

function payloadString(payload: Record<string, unknown>, key: string): string {
  const v = payload[key]
  return typeof v === 'string' ? v : ''
}

export function Programs() {
  const [items, setItems] = useState<ProgramApplicationDetail[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<ProgramApplicationFilters>({
    program: 'booth_fund',
    status: 'pending',
    limit: PAGE_SIZE,
    offset: 0,
  })
  const [selected, setSelected] = useState<ProgramApplicationDetail | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getProgramApplications(filters)
      if (res.ok && res.data) {
        setItems(res.data.items)
        setTotal(res.data.total)
      } else {
        setError(res.error?.message || 'Failed to load applications')
      }
    } catch {
      setError('Failed to connect to API')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    load()
  }, [load])

  const openDetail = async (id: string) => {
    const res = await getProgramApplication(id)
    if (res.ok && res.data) setSelected(res.data)
    else setError(res.error?.message || 'Failed to load application')
  }

  const transition = async (
    id: string,
    body: { status?: ProgramApplicationStatus; reviewer_notes?: string }
  ) => {
    const res = await updateProgramApplication(id, body)
    if (res.ok && res.data) {
      setSelected(res.data)
      load()
    } else {
      throw new Error(res.error?.message || 'Update failed')
    }
  }

  const offset = filters.offset ?? 0
  const page = Math.floor(offset / PAGE_SIZE) + 1
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100">
          Programs
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Booth Fund and future campaigns — {total} {filters.status || 'total'}
        </p>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <select
          value={filters.program || ''}
          onChange={(e) =>
            setFilters({ ...filters, program: e.target.value || undefined, offset: 0 })
          }
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {PROGRAM_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          <option value="">All programs</option>
        </select>
        <select
          value={filters.status || ''}
          onChange={(e) =>
            setFilters({
              ...filters,
              status: e.target.value as ProgramApplicationStatus | '',
              offset: 0,
            })
          }
          className="rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] text-gray-900 dark:text-gray-100 px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="mb-6 rounded-xl bg-red-50 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No applications found</div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520]">
          <table className="w-full text-sm">
            <thead className="bg-[#faf6ff] dark:bg-[#221728] text-left text-xs uppercase tracking-wider text-[#7f4f9a] dark:text-[#b58fcc]">
              <tr>
                <th className="px-4 py-3">Applicant</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Fee</th>
                <th className="px-4 py-3">Shop</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => openDetail(row.id)}
                  className="cursor-pointer border-t border-[#f0e3ff] dark:border-[#3d2c4f] hover:bg-[#faf6ff] dark:hover:bg-[#2d2236]"
                >
                  <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                    <div className="font-medium">{row.applicant_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {row.applicant_email}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                    {payloadString(row.payload, 'event_name') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                    {payloadString(row.payload, 'event_date') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                    {formatCurrencyCents(row.payload.table_fee_cents)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                    {row.shop_handle ? (
                      <span>
                        @{row.shop_handle}{' '}
                        {row.shop_has_listing ? (
                          <span className="ml-1 text-emerald-600 dark:text-emerald-400">✓</span>
                        ) : (
                          <span className="ml-1 text-amber-600 dark:text-amber-400">no listing</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-400">no shop yet</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[row.status]}`}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <CustomButton
            onClick={() => setFilters({ ...filters, offset: Math.max(0, offset - PAGE_SIZE) })}
            disabled={page <= 1}
            variant="secondary"
          >
            Previous
          </CustomButton>
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Page {page} of {totalPages}
          </span>
          <CustomButton
            onClick={() => setFilters({ ...filters, offset: offset + PAGE_SIZE })}
            disabled={page >= totalPages}
            variant="secondary"
          >
            Next
          </CustomButton>
        </div>
      )}

      {selected && (
        <ProgramApplicationDetailModal
          application={selected}
          onClose={() => setSelected(null)}
          onTransition={(body) => transition(selected.id, body)}
        />
      )}
    </div>
  )
}
